// onboarding/triggers.ts — 段階開示 トリガー登録表 (flow.md §オンボーディング段階開示 の鏡写し)
//
// 純粋モジュール: DOM / React / localStorage への参照なし。ユニットテスト可能。
//
// 設計原則 (flow.md 決 #19):
//   「各独自概念は中心ループ上で必要になった瞬間に1つだけ提示」
//   - nextReveal は常に 0 または 1 件しか返さない（"1つだけ" の強制）。
//   - liveToday=false の行はシーム: 対応システムが着地したときに true に切替える。
//     呼び出し側のコードは変えない。
//
// 経済平面の分離 (決 econ-3/4):
//   'yen' (圓) と 'rankPt' (段位pt) は別平面・非可換。
//   コピー文字列には両平面を接続する文言を含めない。

export type ConceptId = 'yen' | 'fuda' | 'rankPt' | 'gatekeeper' | 'fukubiki' | 'seatYaku';

export interface ConceptTrigger {
  id: ConceptId;
  /** 中心ループ上でこの概念が初めて必要になるステップ (flow.md アンカー) */
  loopStep: string;
  /** 複数概念が同一 beat で発火した時の優先順 (小さい方を先に表示) = 表の行順 */
  order: number;
  /** ≤1行 初回タッチ コピー (fit-safe: 改行なし) */
  copy: string;
  /** true = 対応ランタイムシステムが現在存在する → nextReveal で返しうる。
   *  false = シーム: 対応システム着地時に true へ切替え。それ以外変更不要。 */
  liveToday: boolean;
}

/**
 * flow.md §オンボーディング段階開示 の初回トリガ表を鏡写しにした定数。
 * order 昇順（表の行順＝開示優先順）で定義。
 *
 * 独自概念6種:
 *  1. 圓 (yen)         — 通貨平面A
 *  2. 招福札 (fuda)    — 札インベントリ
 *  3. 段位pt (rankPt)  — 競技平面（圓とは別平面・非可換）
 *  4. 昇段戦/門番 (gatekeeper)
 *  5. 福引+花牌 (fukubiki)
 *  6. 席役牌 (seatYaku) — 唯一 liveToday=true（80909db で実装済み）
 *
 * 奉納は福引の sub-touch (flow.md l.32) のため独立行を持たない → "1つだけ" 維持。
 */
export const CONCEPT_TRIGGERS: readonly ConceptTrigger[] = [
  {
    id: 'yen',
    loopStep: '圓を「楽しい装飾」に（卓背景・牌背・紙吹雪・卓テーマを奢る）',
    order: 1,
    copy: '圓（えん）は対局で稼ぐ装飾通貨です。招福札とは別の平面で動きます。',
    liveToday: false, // needs: 圓 balance store
  },
  {
    id: 'fuda',
    loopStep: '招福札を授かる（＝越えてきた節目の数）',
    order: 2,
    copy: '招福札は段位の節目で授かる引換券。福引（福牌山）を引く権利になります。',
    liveToday: false, // needs: 札 inventory persistence
  },
  {
    id: 'rankPt',
    loopStep: '通常対局（段位戦／友人戦／ソロ）↓ 段位ptを貯める',
    order: 3,
    copy: '段位ptを貯めると昇段します。圓とは別の競技平面で、互いに変換できません。',
    liveToday: false, // needs: 段位pt progression store
  },
  {
    id: 'gatekeeper',
    loopStep: '大段位の門番との「昇段戦」（専用CPU）',
    order: 4,
    copy: '次の段位の「門番」が現れました。倒せば昇段演出と招福札が授かれます。',
    liveToday: false, // needs: 段位pt progression store + 昇段戦 UI
  },
  {
    id: 'fukubiki',
    loopStep: '福引（福牌山）で、その門番＝立ち絵を迎える',
    order: 5,
    copy: '招福札で福引が引けます。花牌効果・圓の奉納で立ち絵を絞り込めます。',
    liveToday: false, // needs: 福引 screen + 福牌山 + 奉納 圓-sink flow
  },
  {
    id: 'seatYaku',
    loopStep: 'より強い門番／変則卓（5〜7人・席役牌）／福録 へ挑む',
    order: 6,
    copy: '5人以上の卓では席役牌（風・季・花・三元を人数に合わせた役）が有効です。',
    liveToday: true, // system live: PlayerCount + N>4 wall/dead-wall + seat-yaku tables (commit 80909db)
  },
] as const;

/**
 * "1つだけ" ルールを強制する純粋決定関数。
 *
 * @param seen   すでに開示済みの概念 id の集合
 * @param fired  この beat で発火した概念 id の配列
 * @returns      開示すべき概念 (order 最小の未見+発火+liveToday)、なければ null
 *
 * 複数の概念が同一 beat で発火しても、この関数は必ず 0 か 1 件しか返さない。
 * キューイングは呼び出し側の責務（seen に追加して次 beat で再呼出し）。
 */
export function nextReveal(
  seen: ReadonlySet<ConceptId>,
  fired: readonly ConceptId[],
): ConceptTrigger | null {
  const firedSet = new Set(fired);
  // order 昇順に並んでいることを前提に最初にマッチしたものを返す
  for (const trigger of CONCEPT_TRIGGERS) {
    if (trigger.liveToday && firedSet.has(trigger.id) && !seen.has(trigger.id)) {
      return trigger;
    }
  }
  return null;
}
